import {TimezoneService} from '../../../services/timezone.service';
import {CurrentSkedTask} from '../../model/task';
import {SkedTaskProcessorService} from '../../services/sked-task-processor.service';
import {CurrentSkedAction, CurrentSkedActionType} from './current-sked.action';
import {CurrentSkedState, initialCurrentSkedState} from './current-sked.state';

const timezoneService = new TimezoneService();
const skedTaskProcessor = new SkedTaskProcessorService(timezoneService);

export function currentSkedReducer(
  state: CurrentSkedState = initialCurrentSkedState,
  action: CurrentSkedAction
): CurrentSkedState {
  switch (action.type) {
    case CurrentSkedActionType.GET_SUCCESS: {
      const processedTasks = skedTaskProcessor.processSkedTasks(
        action.payload.sked.tasks,
        action.payload.teamSked.tasks
      );

      const combinedSked = {
        ...action.payload.sked,
        tasks: processedTasks,
      };

      const filterKey = action.payload.expired ? 'expired' : 'today';

      return {
        ...state,
        currentSked: {
          ...state.currentSked,
          [filterKey]: combinedSked,
        },
      };
    }
    case CurrentSkedActionType.GET_PROJECT_SUCCESS: {
      const processedTasks = skedTaskProcessor.processSkedTasks(
        action.payload.skedProject.tasks,
        [] // No team sked tasks for project view
      );

      const processedProject = {
        ...action.payload.skedProject,
        tasks: processedTasks,
      };

      const projectId = action.payload.skedProject.projectIds[0];
      const filterKey = action.payload.expired ? 'expired' : 'today';

      return {
        ...state,
        currentSkedProjectsMap: {
          ...state.currentSkedProjectsMap,
          [projectId]: {
            ...state.currentSkedProjectsMap[projectId],
            [filterKey]: processedProject,
          },
        },
      };
    }
    case CurrentSkedActionType.GET_CHALLENGE_SUCCESS: {
      const {csChallengeData} = action.payload;
      const challengeId = csChallengeData.challenge._id;

      return {
        ...state,
        challengeMap: {
          ...state.challengeMap,
          [challengeId]: csChallengeData.challenge,
        },
        challengeDaysMap: {
          ...state.challengeDaysMap,
          [challengeId]: {
            ...(state.challengeDaysMap[challengeId] ?? {}),
            ...(csChallengeData.days ?? {}),
          },
        },
        challengeCyclesMap: {
          ...state.challengeCyclesMap,
          [challengeId]: {
            ...(state.challengeCyclesMap[challengeId] ?? {}),
            ...(csChallengeData.cycles ?? {}),
          },
        },
      };
    }
    case CurrentSkedActionType.GET_CYCLE_DAY_SUCCESS: {
      const {csChallengeData} = action.payload;
      const challengeId = csChallengeData.challenge._id;

      return {
        ...state,
        challengeDaysMap: {
          ...state.challengeDaysMap,
          [challengeId]: {
            ...(state.challengeDaysMap[challengeId] ?? {}),
            ...(csChallengeData.days ?? {}),
          },
        },
      };
    }
    case CurrentSkedActionType.GET_CHALLENGE_CYCLE_SUCCESS: {
      const {projectId, cycle} = action.payload;
      const cycleKey = cycle.cycleStartDate;

      return {
        ...state,
        challengeCyclesMap: {
          ...state.challengeCyclesMap,
          [projectId]: {
            ...(state.challengeCyclesMap[projectId] ?? {}),
            [cycleKey]: cycle,
          },
        },
      };
    }
    case CurrentSkedActionType.GET_CS_CHALLENGE_DAY_DETAIL: {
      const {dateKey, metricId} = action.payload;
      return {
        ...state,
        chartDetailLoadingMap: {
          ...state.chartDetailLoadingMap,
          [dateKey]: {
            ...(state.chartDetailLoadingMap[dateKey] ?? {}),
            [metricId]: true,
          },
        },
      };
    }
    case CurrentSkedActionType.GET_CS_CHALLENGE_DAY_DETAIL_SUCCESS: {
      const {chartDetail} = action.payload;
      const dateKey = chartDetail.date;
      const metricId = chartDetail.metricId;
      return {
        ...state,
        chartDetailMap: {
          ...state.chartDetailMap,
          [dateKey]: {
            ...(state.chartDetailMap[dateKey] ?? {}),
            [metricId]: chartDetail,
          },
        },
        chartDetailLoadingMap: {
          ...state.chartDetailLoadingMap,
          [dateKey]: {
            ...(state.chartDetailLoadingMap[dateKey] ?? {}),
            [metricId]: false,
          },
        },
      };
    }
    case CurrentSkedActionType.GET_CS_CHALLENGE_DAY_DETAIL_FAILURE: {
      const {dateKey, metricId} = action.payload;
      return {
        ...state,
        chartDetailLoadingMap: {
          ...state.chartDetailLoadingMap,
          [dateKey]: {
            ...(state.chartDetailLoadingMap[dateKey] ?? {}),
            [metricId]: false,
          },
        },
      };
    }
    case CurrentSkedActionType.SAVE_CHALLENGE_METRIC_VALUES_SUCCESS: {
      const {csChallengeData} = action.payload;
      if (!csChallengeData?.days) return state;
      const challengeId = csChallengeData.challenge?._id;
      if (!challengeId) return state;

      return {
        ...state,
        challengeDaysMap: {
          ...state.challengeDaysMap,
          [challengeId]: {
            ...(state.challengeDaysMap[challengeId] ?? {}),
            ...csChallengeData.days,
          },
        },
      };
    }
    case CurrentSkedActionType.GET_TASK_SUCCESS:
    case CurrentSkedActionType.CHANGE_TASK_STATUS_SUCCESS:
    case CurrentSkedActionType.CHANGE_SUBTASK_STATUS_SUCCESS:
    case CurrentSkedActionType.UPDATE_SUBTASK_METRICS_SUCCESS:
    case CurrentSkedActionType.ADD_SUBTASK_NOTE_SUCCESS:
    case CurrentSkedActionType.COMPLETE_TASK_SUCCESS:
      return {...state, task: action.payload.task};
    case CurrentSkedActionType.UPDATE_VALUE_CHOSEN:
      return {
        ...state,
        metricUpdateValueChosenMap: {
          ...state.metricUpdateValueChosenMap,
          [action.payload.id]: true,
        },
      };
    case CurrentSkedActionType.CLEAR_VALUE_CHOSEN:
      return {...state, metricUpdateValueChosenMap: {}};
    case CurrentSkedActionType.UPDATE_METRIC_FORM_STATE: {
      const {taskId, subtaskId, formState} = action.payload;
      return {
        ...state,
        metricFormStateMap: {
          ...state.metricFormStateMap,
          [taskId]: {
            ...state.metricFormStateMap[taskId],
            [subtaskId]: formState,
          },
        },
      };
    }
    case CurrentSkedActionType.CLEAR_METRIC_FORM_STATE: {
      const {taskId, subtaskId} = action.payload;
      const taskFormStates = {...state.metricFormStateMap[taskId]};
      delete taskFormStates[subtaskId];

      return {
        ...state,
        metricFormStateMap: {
          ...state.metricFormStateMap,
          [taskId]: taskFormStates,
        },
      };
    }
    case CurrentSkedActionType.CLEAR_TASK:
      return {...state, task: null};
    case CurrentSkedActionType.UPDATE_SKED_CONTEXT:
      return {...state, skedContext: action.payload.skedContext};
    case CurrentSkedActionType.CLEAR: {
      return {
        skedContext: null,
        currentSked: {},
        currentSkedProjectsMap: {},
        task: null,
        updatedTask: {} as CurrentSkedTask,
        metricUpdateValueChosenMap: {},
        metricFormStateMap: {},
        challengeMap: {},
        challengeDaysMap: {},
        challengeCyclesMap: {},
        chartDetailMap: {},
        chartDetailLoadingMap: {},
      };
    }
    default:
      return state;
  }
}
